import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  CircularProgress,
  FormControl,
  GKDatePicker,
  GKModal,
  GKSearchSelect,
  GKTextField,
  Grid2,
} from "components";
import dayjs from "dayjs";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { INDIA_STATES } from "utils/constants";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  revenueData?: any;
  refetch: any;
}

const RevenueModal = (props: Props) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { open, setOpen, revenueData, refetch } = props;

  const { mutate, isLoading } = useMutation({
    mutationKey: ["CREATE_BILLING"],
    mutationFn: (data: any) =>
      axiosInstance.post(`/super_admin/${id}/revenue/`, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["GET_PORTFOLIO_LIMITS"] });
      queryClient.invalidateQueries({ queryKey: ["GET_ADMIN_DETAILS"] });
      refetch();
      setOpen(false);
      toast.success(response?.data?.message || "Update Successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      amcCharges: revenueData?.amcCharges || "",
      portfolioLimit: revenueData?.portfolioLimit || "",
      perPortfolioCharges: revenueData?.perPortfolioCharges || "",
      renewalDate: revenueData?.renewalDate
        ? dayjs(revenueData.renewalDate)
        : null,
      state:
        INDIA_STATES?.find((s) => s.name === revenueData?.state)?.value ||
        revenueData?.state ||
        "",
    },
    onSubmit: (values) => {
      mutate({
        ...values,
        renewalDate: values.renewalDate
          ? dayjs(values.renewalDate).format("YYYY-MM-DD")
          : null,
      });
    },
  });

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle={"Edit"}
      width={"400px"}
      footer={
        <Grid2
          container
          size={12}
          justifyContent={"flex-end"}
          alignContent={"center"}
          gap={1}
        >
          <Button variant="lightBg" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </Grid2>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                type="number"
                inputLabel="Amc Charges"
                onChange={formik?.handleChange}
                value={formik.values.amcCharges}
                name="amcCharges"
              />
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                type="number"
                inputLabel="Portfolio Limit"
                onChange={formik?.handleChange}
                value={formik.values.portfolioLimit}
                name="portfolioLimit"
              />
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                type="number"
                inputLabel="Per Portfolio Charges"
                onChange={formik?.handleChange}
                value={formik.values.perPortfolioCharges}
                name="perPortfolioCharges"
              />
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
            <FormControl variant="standard" fullWidth>
              <GKDatePicker
                inputLabel="Renewal Date"
                name="renewalDate"
                value={formik.values.renewalDate}
                onChange={(value) => formik.setFieldValue("renewalDate", value)}
                format="DD/MM/YYYY"
              />
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
            <GKSearchSelect
              inputLabel="State"
              options={INDIA_STATES || []}
              value={(() => {
                const stateValue = formik.values.state;
                return (
                  INDIA_STATES?.find(
                    (state) =>
                      state.value === stateValue?.toLowerCase() ||
                      state.name === stateValue
                  ) || null
                );
              })()}
              onChange={(_, val) =>
                formik.setFieldValue("state", val?.value || val)
              }
              getOptionLabel={(opt) =>
                typeof opt === "object" ? opt.name : String(opt)
              }
              renderInput={(params) => (
                <GKTextField
                  {...params}
                  required
                  variant="outlined"
                  placeholder="Select State"
                  fullWidth
                />
              )}
            />
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default RevenueModal;
